import { createFileRoute } from '@tanstack/react-router';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import {
  Section,
} from '@/features/design-system/showcase-primitives';
import { ROOM_PALETTE } from '@/features/editor/room-palette';

export const Route = createFileRoute('/design-system/brand')({
  component: BrandPage,
});

const VOCAB: ReadonlyArray<[string, string, string]> = [
  ['Plan', 'Project, floorplan project', 'Top-level container — one PDF.'],
  ['Floor', 'Page, level', 'A single floorplan image inside a Plan.'],
  ['Room', 'Zone, area, space, label', 'Polygon-shaped grouping on a Floor.'],
  [
    'Placed Item',
    'Item, dot, point, marker, pin',
    'A specific Light/Socket/Network point on the canvas.',
  ],
  ['Fixture', 'Product, lamp, device, SKU', 'Catalog entry assigned to an Item.'],
  ['Catalog', 'Library, fixture library, products', 'The list of available fixtures.'],
  ['Snapshot', 'Version copy, freeze', 'Frozen state of a Plan at a point in time.'],
  ['Milestone', 'Tag, named version', 'A user-named Snapshot.'],
  ['Auto-Checkpoint', 'Auto-save, draft snapshot', 'Continuous background save.'],
  ['Branch', 'Workspace, fork', 'Parallel line of edits.'],
  ['Restore', 'Revert, roll back', 'Bring back a previous Snapshot.'],
  ['Accept (Change)', 'Apply, merge, take', 'Take the change in a Diff review.'],
  ['Diff', 'Compare', 'Difference between two Snapshots.'],
];

const GOOD_COPY: ReadonlyArray<[string, string]> = [
  ['No plans yet', 'Empty state title — sentence case, terse.'],
  [
    'Start by uploading a floorplan to plan your home’s lights, sockets, and network points.',
    'Empty state body — second-person, concrete.',
  ],
  ['Drop a floorplan image here, or click to browse', 'File picker — imperative.'],
  ['Saving…', 'In-progress state — ellipsis glyph.'],
  ['Could not save the change.', 'Friendly fallback error.'],
  ['+ New', 'Additive prefix; sentence case.'],
];

const BAD_COPY: ReadonlyArray<[string, string]> = [
  [
    'Save Milestone',
    'Title-case is wrong. Use "Save milestone".',
  ],
  [
    'AI-powered planning at your fingertips',
    'Marketing voice. Not for this product.',
  ],
  ['🎉 Saved!', 'No emoji, anywhere.'],
  ['Couldn’t save 🙁', 'Same — no emoji even in errors.'],
  ['Click here to delete', 'Imperative without "Delete" loses the verb.'],
];

function BrandPage() {
  return (
    <div className="flex flex-col gap-14">
      <header className="flex flex-col gap-2">
        <Text variant="eyebrow">Brand</Text>
        <Text variant="display" as="h1">
          Logo, voice, vocabulary
        </Text>
        <Text variant="body" tone="muted" className="max-w-2xl">
          The mark blends a bulb, an electrical socket (filament reads as
          contact pins), and an RJ45 connector (the bulb base is reshaped into
          the keep tab). One glyph carries all three domains.
        </Text>
      </header>

      <Section
        eyebrow="Logo"
        title="Wordmark + mark"
        description="Used in the editor header and at the top of any external surface (sign-in, error pages)."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex h-32 items-center justify-center rounded-xl border border-border bg-background p-6">
            <img
              src="/logo.svg"
              alt="Lights Planner"
              className="h-9 text-foreground"
            />
          </div>
          <div className="flex h-32 items-center justify-center rounded-xl border border-border bg-foreground p-6">
            <img
              src="/logo.svg"
              alt="Lights Planner"
              className="h-9 text-background"
              style={{ filter: 'invert(1)' }}
            />
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Logo"
        title="Mark only & favicon"
        description="The mark works without the wordmark for tight spots — favicons, app icon, header in compact viewports."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-xl border border-border bg-background p-6">
            <img
              src="/logo-mark.svg"
              alt="Lights Planner mark"
              className="size-12 text-foreground"
            />
            <Text variant="caption">/logo-mark.svg</Text>
          </div>
          <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-xl border border-border bg-foreground p-6">
            <img
              src="/logo-mark.svg"
              alt=""
              aria-hidden
              className="size-12"
              style={{ filter: 'invert(1)' }}
            />
            <Text variant="caption" className="text-background/80">
              on dark surface
            </Text>
          </div>
          <div className="flex h-32 flex-col items-center justify-center gap-2 rounded-xl border border-border bg-background p-6">
            <img
              src="/favicon.svg"
              alt="Favicon"
              className="size-10 rounded"
            />
            <Text variant="caption">/favicon.svg</Text>
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Voice"
        title="Tone summary"
        description="Plain. Second-person. Sentence case. Terse. Precise about domain terms."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Card size="sm">
            <CardHeader>
              <CardTitle>Direct</CardTitle>
              <CardDescription>
                You haven&apos;t created any plans yet. Not: &quot;Looks like
                you don&apos;t have any plans yet&quot;.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle>Imperative</CardTitle>
              <CardDescription>
                Save milestone. Publish. Fit to floor. Send magic link. The
                button is the verb.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card size="sm">
            <CardHeader>
              <CardTitle>No marketing</CardTitle>
              <CardDescription>
                No &quot;powerful&quot;, &quot;delightful&quot;,
                &quot;AI-powered&quot;. The product does its job; the copy
                names the job.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </Section>

      <Section
        eyebrow="Voice"
        title="Do / don't"
        description="Lifted from the codebase. The fallback errors and empty states are the test set."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="default">Do</Badge>
              <Text variant="label">Lifted from the codebase</Text>
            </div>
            <div className="flex flex-col gap-2">
              {GOOD_COPY.map(([copy, why]) => (
                <div
                  key={copy}
                  className="rounded-md border border-border bg-card p-3"
                >
                  <Text variant="body">{copy}</Text>
                  <Text variant="caption">{why}</Text>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="destructive">Don&apos;t</Badge>
              <Text variant="label">Anti-patterns</Text>
            </div>
            <div className="flex flex-col gap-2">
              {BAD_COPY.map(([copy, why]) => (
                <div
                  key={copy}
                  className="rounded-md border border-border bg-card p-3"
                >
                  <Text variant="body" tone="muted" className="line-through">
                    {copy}
                  </Text>
                  <Text variant="caption">{why}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section
        eyebrow="Vocabulary"
        title="Ubiquitous language"
        description="Every product term has one canonical form. UBIQUITOUS_LANGUAGE.md is the full glossary; the repo's lints flag synonym drift."
      >
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">
                  Canonical
                </th>
                <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">
                  Avoid
                </th>
                <th className="px-3 py-2 text-start text-xs font-medium text-muted-foreground">
                  Meaning
                </th>
              </tr>
            </thead>
            <tbody>
              {VOCAB.map(([canon, avoid, meaning], i) => (
                <tr
                  key={canon}
                  className={
                    i % 2 === 0 ? 'bg-card' : 'bg-card/40'
                  }
                >
                  <td className="px-3 py-2 align-top">
                    <Text variant="label">{canon}</Text>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Text variant="code" tone="muted">
                      {avoid}
                    </Text>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <Text variant="caption">{meaning}</Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        eyebrow="Brand"
        title="Room palette"
        description="The 16 colors a user can pick from when creating a Room. Hand-picked so several rooms can share a canvas without collision."
      >
        <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card p-4">
          {ROOM_PALETTE.map((color, idx) => (
            <div
              key={color}
              className="flex flex-col items-center gap-1.5"
              style={{ width: 48 }}
            >
              <span
                aria-hidden
                className="size-9 rounded-full ring-1 ring-foreground/10"
                style={{ background: color }}
              />
              <Text variant="caption">{idx + 1}</Text>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
